/**
 * HubSpot tracking helpers shared across the apps.
 *
 * The HubSpot loader sets a persistent `hubspotutk` cookie on the apex domain
 * (e.g. `.nativeteams.com`) which binds the visitor to whatever email we last
 * called `_hsq.push(['identify', { email }])` with. On a shared device this
 * causes subsequent form submissions from a different user to be merged onto
 * the previous user's HubSpot contact (their email is added as a secondary
 * email instead of creating a new contact).
 */

interface HubSpotIdentifyTraits {
  email: string;
  [key: string]: string | number | boolean | null;
}

type HubSpotQueueCommand =
  | ['identify', HubSpotIdentifyTraits]
  | ['trackPageView']
  | ['setPath', string];

declare global {
  interface Window {
    _hsq?: HubSpotQueueCommand[];
  }
}

const HUBSPOT_COOKIE_NAMES = ['hubspotutk', '__hstc', '__hssc', '__hssrc'] as const;

function getDomainCandidates(hostname: string): string[] {
  if (!hostname || hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return [];
  }

  const parts = hostname.split('.');
  const candidates: string[] = [];

  for (let i = 0; i < parts.length - 1; i++) {
    const domain = parts.slice(i).join('.');
    candidates.push(domain, `.${domain}`);
  }

  return candidates;
}

function deleteCookieEverywhere(name: string): void {
  if (typeof document === 'undefined') return;

  const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const domains = getDomainCandidates(window.location.hostname);

  document.cookie = `${name}=; expires=${past}; path=/; SameSite=Lax`;

  for (const domain of domains) {
    document.cookie = `${name}=; expires=${past}; path=/; domain=${domain}; SameSite=Lax`;
  }
}

/**
 * Revokes HubSpot consent and wipes HubSpot tracking cookies on every parent
 * domain so the next user on the same browser starts as a fresh, anonymous
 * HubSpot visitor.
 *
 * Safe to call from any client component / browser-side code. No-ops on the
 * server and when HubSpot has not loaded yet (the manual cookie cleanup still
 * runs, which is the primary safety net).
 */
export function resetHubSpotVisitor(): void {
  if (typeof window === 'undefined') return;

  for (const name of HUBSPOT_COOKIE_NAMES) {
    deleteCookieEverywhere(name);
  }
}

/**
 * Resets the HubSpot visitor state and identifies the current user. Always
 * resets before identifying to ensure a clean visitor state on every login.
 *
 * Returns `true` when the identify call was queued, `false` when skipped
 * (no email provided or SSR).
 */
export function identifyHubSpotUser(email: string | null | undefined): boolean {
  if (typeof window === 'undefined' || !email) return false;

  const normalized = email.trim().toLowerCase();
  if (!normalized) return false;

  resetHubSpotVisitor();

  window._hsq = window._hsq || [];
  window._hsq.push(['identify', { email: normalized }]);
  window._hsq.push(['trackPageView']);

  return true;
}
